// src/pages/projects/AustraliaWildfireAnalysis.jsx
import React from "react";
import { ProjectLayout } from "../../components/layout/ProjectLayout";
import "../../components/styles/caseStudy.css";

const Stat = ({ value, label }) => (
  <div className="cs_stat">
    <span className="cs_stat_value">{value}</span>
    <span className="cs_stat_label">{label}</span>
  </div>
);

const Finding = ({ number, question, finding }) => (
  <div className="cs_finding">
    <span className="cs_finding_number">{number}</span>
    <div className="cs_finding_body">
      <p className="cs_finding_question">{question}</p>
      <p className="cs_finding_answer">{finding}</p>
    </div>
  </div>
);

const AustraliaWildfireAnalysis = () => {
  return (
    <ProjectLayout
      title="Australia Wildfire Activity Analysis"
      context="Fifteen years of NASA FIRMS/MODIS satellite data examined for risk patterns, seasonal trends, and regional concentration across seven Australian regions."
      tools={["Python", "Pandas", "Matplotlib", "Seaborn", "Plotly Dash", "Folium"]}
      repoUrl="https://github.com/abranco0403/australia-wildfire-analysis"
    >
      <div className="cs_content">

        {/* ── Stats Row ── */}
        <div className="cs_stats_row">
          <Stat value="15 yrs" label="Data period (2005–2020)" />
          <Stat value="7" label="Australian regions" />
          <Stat value="1M+" label="Satellite detections" />
          <Stat value="r ≈ +0.28" label="Confidence correlation" />
        </div>

        {/* ── Business Questions ── */}
        <div className="cs_section">
          <h2 className="cs_section_title">Business Questions</h2>
          <ul className="cs_list">
            <li>Has wildfire severity changed over time — and is 2019–20 truly an outlier?</li>
            <li>Which regions carry the most fire risk, and by how much?</li>
            <li>How reliable is the satellite detection data at the extremes that matter most?</li>
          </ul>
        </div>

        {/* ── Approach ── */}
        <div className="cs_section">
          <h2 className="cs_section_title">Approach</h2>
          <ol className="cs_list cs_list--ordered">
            <li>Loaded NASA FIRMS dataset directly via URL — no local file dependency.</li>
            <li>Parsed dates and extracted year/month components for time-series aggregation.</li>
            <li>Analysed annual fire area trends to identify structural breaks vs gradual change.</li>
            <li>Mapped regional distribution using pixel count and radiative power metrics.</li>
            <li>Calculated Pearson correlation between detection confidence and fire intensity.</li>
            <li>Built an interactive Dash dashboard for region and year exploration.</li>
          </ol>
        </div>

        {/* ── Key Findings ── */}
        <div className="cs_section">
          <h2 className="cs_section_title">Key Findings</h2>
          <div className="cs_findings">
            <Finding
              number="01"
              question="How has severity changed over time?"
              finding="The 2019–20 Black Summer season is a structural break, not the continuation of a gradual trend. Fire area exceeded every prior year by a wide margin — burning roughly 18.6 million hectares. Risk models assuming linear escalation will systematically underestimate tail exposure. Seasonality (Dec–Feb peak) is stable and reliable every year."
            />
            <Finding
              number="02"
              question="Which regions carry the most risk?"
              finding="Queensland and the Northern Territory dominate on both intensity and spatial footprint, accounting for the majority of all fire pixel detections. Two regions alone account for ~58% of all detections. Tasmania and Victoria have the smallest footprint but the highest population exposure — a different kind of risk."
            />
            <Finding
              number="03"
              question="How reliable is the detection data?"
              finding="Pearson r ≈ +0.28 — a weak positive correlation between radiative power and detection confidence. More intense fires are detected with slightly higher confidence, which is a reassuring property: data quality is marginally better at the extremes where precision matters most. Fire brightness is right-skewed — normal distribution assumptions will underestimate tail events."
            />
          </div>
        </div>

        {/* ── Summary Table ── */}
        <div className="cs_section">
          <h2 className="cs_section_title">Summary</h2>
          <div className="cs_table_wrapper">
            <table className="cs_table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Finding</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Severity over time</td>
                  <td>2019–20 is a structural break — Dec–Feb seasonality is stable</td>
                </tr>
                <tr>
                  <td>Regional risk</td>
                  <td>QLD + NT dominate footprint · TAS + VIC have highest population exposure</td>
                </tr>
                <tr>
                  <td>Data reliability</td>
                  <td>r ≈ +0.28 · marginally better at extremes · right-skewed distribution</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Live Dashboard CTA ── */}
        <div className="cs_section">
          <h2 className="cs_section_title">Interactive Dashboard</h2>
          <p className="cs_body_text">
            The static analysis answers fixed questions. The Dash dashboard allows free exploration —
            compare any region's fire season against any year, or watch how monthly patterns shift
            between 2005 and 2020.
          </p>
          <a
            href="https://aus-wildfire-analysis.netlify.app"
            target="_blank"
            rel="noopener noreferrer"
            className="cs_cta"
          >
            Open Live Dashboard ↗
          </a>
        </div>

      </div>
    </ProjectLayout>
  );
};

export default AustraliaWildfireAnalysis;
